import React, { useEffect, useState } from 'react';
import { useNotifications } from '../contexts/NotificationsContext';
import { useSearchParams } from 'react-router-dom';
import { 
  Box, 
  Paper, 
  Typography, 
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
  List,
  ListItem,
  Chip,
  Container,
  MenuItem,
  useMediaQuery,
  Tooltip as MUITooltip,
  Skeleton,
  LinearProgress,
  Avatar,
} from '@mui/material';
import {
  Person,
  MoreVert,
  ArrowUpward,
  ArrowDownward,
  AccountBalance,
  TrendingUp,
  Close,
  Lightbulb,
  TipsAndUpdates,
  EmojiEvents,
  Speed,
  Groups,
  CalendarMonth,
  Refresh,
} from '@mui/icons-material';
import { supabase } from '../lib/supabase';
import { dedupeCollectionCases } from '../utils/loandiskId';
import { useAuth } from '../contexts/AuthContext';
import { PostgrestResponse } from '@supabase/supabase-js';
import { format, startOfWeek, endOfWeek, subWeeks, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { useTheme } from '@mui/material/styles';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { styled } from '@mui/material/styles';
import NotificationsPanel from '../components/NotificationsPanel';
import { getContextualTip, type CollectionTip } from '../data/collectionTips';
import { fetchAllPaginated } from '../utils/supabasePagination';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend
);

// Format currency helper
const formatCurrency = (value: number | string | null): string => {
  if (!value) return '0.00';
  return Number(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

interface BorrowerData {
  name: string;
  mobile_no: string;
  email: string;
  address: string;
}

interface CollectionCase {
  id: string;
  account_balance: number;
  status: string;
  loandisk_id: string;
  agent_id: string | null;
  borrower: {
    name: string;
    mobile_no: string;
    email: string;
    address: string;
  };
}

// Add this interface for the raw data from Supabase
interface RawCollectionData {
  id: string;
  account_balance: number;
  status: string;
  loandisk_id: string | null;
  agent_id: string | null;
  borrower: {
    name: string;
    mobile_no: string;
    email: string;
    address: string;
  } | {
    name: string;
    mobile_no: string;
    email: string;
    address: string;
  }[];
}

// Data coming directly from Supabase. Note: Depending on your relationship,
// the joined borrower data might come as an array or an object.
interface ForCollectionData {
  id: string;
  borrower_id: string;
  account_balance: number;
  status: string;
  agent_name: string | null;
  loandisk_id: string | null;
  borrower: BorrowerData[] | BorrowerData;
}

interface AssignmentFormData {
  loandisk_id: string;
  agent_name: string;
}

interface CollectorSummary {
  collector_id: string;
  collector_name: string;
  avatar_url?: string | null;
  total_collections: number;
  this_week: number;
  last_week: number;
  this_month: number;
  last_month: number;
  week_change_pct: number;
  month_change_pct: number;
}

interface MonthlyCollectionData {
  month: string;
  amount: number;
}

// Add these helper components at the top
const StatCard = ({ title, value, icon, trend, trendValue, color }: any) => {
  const theme = useTheme();
  
  return (
    <Card 
      elevation={0}
      sx={{ 
        height: '100%',
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: `1px solid ${color}25`,
        transition: 'transform 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
        }
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ 
            p: 0.75,
            borderRadius: 1.5,
            bgcolor: `${color}15`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {icon}
          </Box>
          <IconButton size="small" sx={{ p: 0.5 }}>
            <MoreVert fontSize="small" />
          </IconButton>
        </Box>
        
        <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 'bold' }}>
          {value}
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            color: trend === 'up' ? theme.palette.success.main : theme.palette.error.main 
          }}>
            {trend === 'up' ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
            <Typography variant="caption" sx={{ ml: 0.5 }}>
              {trendValue}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// Add this styled component for table headers
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.paper,
  borderBottom: `2px solid ${theme.palette.divider}`,
  padding: theme.spacing(1.5),
  whiteSpace: 'nowrap'
}));

// Add this interface for agents
interface Agent {
  id: string;
  username: string;
  role: string;
}

const CollectionDashboard: React.FC = () => {
  const { notifications, markAsRead, setNotifications, markAllAsRead, getCollectionIdFromNotification } = useNotifications();
  const { user, userProfile } = useAuth();
  const [searchParams] = useSearchParams();
  const borrowerId = searchParams.get('borrower');
  const [unassignedCases, setUnassignedCases] = useState<CollectionCase[]>([]);
  const [collectorSummaries, setCollectorSummaries] = useState<CollectorSummary[]>([]);
  const [totalMonthlyCollection, setTotalMonthlyCollection] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [selectedCase, setSelectedCase] = useState<CollectionCase | null>(null);
  const [formData, setFormData] = useState<AssignmentFormData>({
    loandisk_id: '',
    agent_name: ''
  });
  const [error, setError] = useState<string | null>(null);
  const [monthlyCollections, setMonthlyCollections] = useState<MonthlyCollectionData[]>([]);
  const theme = useTheme();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loadingMonthly, setLoadingMonthly] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [currentTip, setCurrentTip] = useState<CollectionTip>(getContextualTip());
  const [teamInsights, setTeamInsights] = useState<{
    teamThisWeek: number;
    teamLastWeek: number;
    teamThisMonth: number;
    teamLastMonth: number;
    teamWeekChangePct: number;
    teamMonthChangePct: number;
    topPerformer: string;
    needsAttention: string[];
  }>({
    teamThisWeek: 0,
    teamLastWeek: 0,
    teamThisMonth: 0,
    teamLastMonth: 0,
    teamWeekChangePct: 0,
    teamMonthChangePct: 0,
    topPerformer: '',
    needsAttention: [],
  });
  const [expandedAgent, setExpandedAgent] = useState<string | null>(null);
  const [todayByAgent, setTodayByAgent] = useState<Record<string, number>>({});
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [supervisorTip] = useState<CollectionTip>(getContextualTip());

  useEffect(() => {
    fetchUnassignedCases();
    fetchCollectionSummaries();
    fetchMonthlyCollections();
    fetchAgents();
    fetchNotifications();
    fetchTodayCollections();

    // Get the current user ID for subscriptions
    const getCurrentUserId = async () => {
      try {
        const { data } = await supabase.auth.getUser();
        return data?.user?.id;
      } catch (error) {
        console.error('Error getting user ID for subscription:', error);
        return null;
      }
    };

    // Set up real-time subscription for notifications
    const setupSubscription = async () => {
      const userId = await getCurrentUserId();
      if (!userId) {
        console.error('Cannot set up subscription - no user ID');
        return;
      }

      console.log('Setting up notification subscription for user:', userId);
      
      const subscription = supabase
        .channel('notifications-changes-collection')
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'notifications',
            filter: `recipient_id=eq.${userId}`
          },
          (payload) => {
            console.log('Real-time notification update:', payload);
            
            // Handle different payload events
            if (payload.eventType === 'INSERT') {
              const newNotification = payload.new as any;
              
              // Only add relevant notification types
              if (
                newNotification.notification_type === 'collection_case' || 
                newNotification.notification_type === 'comment_mention' || 
                newNotification.notification_type === 'update_mention' ||
                newNotification.notification_type === 'reminder'
              ) {
                console.log('Adding new notification:', newNotification);
                setNotifications(prev => [newNotification, ...prev]);
                
                // Show browser notification for mentions and reminders if supported
                if ((newNotification.notification_type === 'comment_mention' || 
                     newNotification.notification_type === 'update_mention' ||
                     newNotification.notification_type === 'reminder') &&
                    window.Notification && 
                    Notification.permission === 'granted') {
                  new Notification('Samofund', {
                    body: newNotification.message,
                    icon: '/samofund-logo.png'
                  });
                }
              }
            } 
            else if (payload.eventType === 'UPDATE') {
              // Update the notification in the list
              console.log('Updating notification:', payload.new);
              setNotifications(prev => 
                prev.map(notif => 
                  notif.id === payload.new.id ? payload.new as any : notif
                )
              );
            } 
            else if (payload.eventType === 'DELETE') {
              // Remove the notification from the list
              console.log('Removing notification:', payload.old);
              setNotifications(prev => 
                prev.filter(notif => notif.id !== payload.old.id)
              );
            }
          }
        )
        .subscribe((status) => {
          console.log('Subscription status:', status);
        });

      return subscription;
    };

    // Set up the subscription
    let subscription: any;
    setupSubscription().then(sub => {
      subscription = sub;
    });

    return () => {
      if (subscription) {
        console.log('Unsubscribing from notifications channel');
        subscription.unsubscribe();
      }
    };
  }, []);

  useEffect(() => {
    console.log('Loading:', loading);
    console.log('Unassigned cases:', unassignedCases);
  }, [loading, unassignedCases]);

  const fetchUnassignedCases = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('for_collection')
        .select(`
          id,
          account_balance,
          status,
          loandisk_id,
          agent_id,
          borrower:borrowers!inner(
            name,
            mobile_no,
            email,
            address
          )
        `)
        .is('agent_id', null)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (data) {
        const transformedData: CollectionCase[] = (data as RawCollectionData[]).map(item => ({
          id: item.id,
          account_balance: Number(item.account_balance),
          status: item.status,
          loandisk_id: item.loandisk_id || '',
          agent_id: item.agent_id,
          borrower: Array.isArray(item.borrower) 
            ? {
                name: item.borrower[0].name,
                mobile_no: item.borrower[0].mobile_no,
                email: item.borrower[0].email,
                address: item.borrower[0].address
              }
            : item.borrower
        }));

        setUnassignedCases(dedupeCollectionCases(transformedData));
      }
    } catch (error) {
      console.error('Error fetching unassigned cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCollectionSummaries = async () => {
    try {
      setLoading(true);
      const now = new Date();

      // Date ranges
      const thisMonthStart = startOfMonth(now);
      const thisMonthEnd = endOfMonth(now);
      const lastMonthStart = startOfMonth(subMonths(now, 1));
      const lastMonthEnd = endOfMonth(subMonths(now, 1));
      const thisWeekStart = startOfWeek(now, { weekStartsOn: 1 });
      const thisWeekEnd = endOfWeek(now, { weekStartsOn: 1 });
      const lastWeekStart = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
      const lastWeekEnd = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });

      // Get all agent profiles (role = 'agent' only — ranked agents)
      const { data: collectorsData, error: collectorsError } = await supabase
        .from('profiles')
        .select('id, username, avatar_url')
        .eq('role', 'agent');

      if (collectorsError) throw collectorsError;
      if (!collectorsData || collectorsData.length === 0) return;

      // Fast lookup maps
      const agentById: Record<string, { id: string; username: string; avatar_url?: string | null }> = {};
      const agentByName: Record<string, { id: string; username: string; avatar_url?: string | null }> = {};
      collectorsData.forEach(a => {
        agentById[a.id] = a;
        agentByName[a.username.toLowerCase()] = a;
      });

      // Use borrower_updates as the data source — same table the BorrowerUpdates page
      // reads, so it is always accessible to supervisors regardless of branch RLS on
      // receipts_unified.  Amounts are parsed from the trigger-generated content string.
      const updateData = await fetchAllPaginated<{
        created_at: string;
        content: string | null;
        agent_name: string | null;
        agent_id: string | null;
      }>((from, to) =>
        supabase
          .from('borrower_updates')
          .select('created_at, content, agent_name, agent_id')
          .ilike('content', 'Payment posted:%')
          .gte('created_at', lastMonthStart.toISOString())
          .lte('created_at', thisMonthEnd.toISOString())
          .is('deleted_at', null)
          .order('created_at', { ascending: true })
          .range(from, to)
      );

      // Bucket every payment into the correct agent
      const agentPayments: Record<string, { date: string; amount: number }[]> = {};
      collectorsData.forEach(a => { agentPayments[a.id] = []; });

      (updateData || []).forEach((u: any) => {
        const amountMatch = u.content?.match(/Payment posted:\s*([\d.]+)/);
        if (!amountMatch) return;
        const amount = Number(amountMatch[1]) || 0;
        if (amount <= 0) return;

        // Resolve collector: agent_name is the most reliable field
        let agentId: string | null = null;
        if (u.agent_name) {
          const profile = agentByName[u.agent_name.toLowerCase()];
          if (profile) agentId = profile.id;
        }
        // Fallback: agent_id might be the collector's UUID when collector_id was set
        if (!agentId && u.agent_id && agentById[u.agent_id]) {
          agentId = u.agent_id;
        }

        if (agentId && agentPayments[agentId]) {
          agentPayments[agentId].push({ date: u.created_at, amount });
        }
      });

      // Compute per-period totals for each agent
      const collectorsSummaries: CollectorSummary[] = collectorsData.map(collector => {
        let thisMonth = 0, lastMonth = 0, thisWeek = 0, lastWeek = 0;

        agentPayments[collector.id]?.forEach(r => {
          const d = new Date(r.date);
          if (d >= thisMonthStart && d <= thisMonthEnd) thisMonth += r.amount;
          if (d >= lastMonthStart && d <= lastMonthEnd) lastMonth += r.amount;
          if (d >= thisWeekStart && d <= thisWeekEnd) thisWeek += r.amount;
          if (d >= lastWeekStart && d <= lastWeekEnd) lastWeek += r.amount;
        });

        const weekChangePct = lastWeek > 0 ? ((thisWeek - lastWeek) / lastWeek) * 100 : (thisWeek > 0 ? 100 : 0);
        const monthChangePct = lastMonth > 0 ? ((thisMonth - lastMonth) / lastMonth) * 100 : (thisMonth > 0 ? 100 : 0);

        return {
          collector_id: collector.id,
          collector_name: collector.username,
          avatar_url: collector.avatar_url ?? null,
          total_collections: thisMonth,
          this_week: thisWeek,
          last_week: lastWeek,
          this_month: thisMonth,
          last_month: lastMonth,
          week_change_pct: weekChangePct,
          month_change_pct: monthChangePct,
        };
      });

      const sortedSummaries = collectorsSummaries.sort((a, b) =>
        b.total_collections - a.total_collections
      );

      setCollectorSummaries(sortedSummaries);

      const total = sortedSummaries.reduce((sum, summary) =>
        sum + summary.total_collections, 0
      );
      setTotalMonthlyCollection(total);

      const teamThisWeek = sortedSummaries.reduce((s, c) => s + c.this_week, 0);
      const teamLastWeek = sortedSummaries.reduce((s, c) => s + c.last_week, 0);
      const teamThisMonth = sortedSummaries.reduce((s, c) => s + c.this_month, 0);
      const teamLastMonth = sortedSummaries.reduce((s, c) => s + c.last_month, 0);
      const teamWeekChangePct = teamLastWeek > 0 ? ((teamThisWeek - teamLastWeek) / teamLastWeek) * 100 : (teamThisWeek > 0 ? 100 : 0);
      const teamMonthChangePct = teamLastMonth > 0 ? ((teamThisMonth - teamLastMonth) / teamLastMonth) * 100 : (teamThisMonth > 0 ? 100 : 0);

      const topPerformer = sortedSummaries.length > 0 ? sortedSummaries[0].collector_name : '';
      const needsAttention = sortedSummaries
        .filter(c => c.week_change_pct < -20 || (c.this_week === 0 && c.last_week > 0))
        .map(c => c.collector_name);

      setTeamInsights({
        teamThisWeek,
        teamLastWeek,
        teamThisMonth,
        teamLastMonth,
        teamWeekChangePct,
        teamMonthChangePct,
        topPerformer,
        needsAttention,
      });

    } catch (error) {
      console.error('Error fetching collection summaries:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyCollections = async () => {
    try {
      setLoadingMonthly(true);
      const now = new Date();
      const yearStart = new Date(now.getFullYear(), 0, 1);

      // Get all agent profiles
      const { data: collectorsData, error: collectorsError } = await supabase
        .from('profiles')
        .select('id, username')
        .eq('role', 'agent');

      if (collectorsError) throw collectorsError;
      if (!collectorsData || collectorsData.length === 0) return;

      const agentByName = new Set(collectorsData.map(c => c.username.toLowerCase()));
      const agentIds = new Set(collectorsData.map(c => c.id));

      // Use borrower_updates — same accessible source as BorrowerUpdates page
      const data = await fetchAllPaginated<{
        created_at: string;
        content: string | null;
        agent_name: string | null;
        agent_id: string | null;
      }>((from, to) =>
        supabase
          .from('borrower_updates')
          .select('created_at, content, agent_name, agent_id')
          .ilike('content', 'Payment posted:%')
          .gte('created_at', yearStart.toISOString())
          .lte('created_at', now.toISOString())
          .is('deleted_at', null)
          .order('created_at', { ascending: true })
          .range(from, to)
      );

      const monthlyData: { [key: string]: number } = {};
      for (let i = 0; i < 12; i++) {
        monthlyData[format(new Date(now.getFullYear(), i, 1), 'MMM')] = 0;
      }

      (data || []).forEach((u: any) => {
        const amountMatch = u.content?.match(/Payment posted:\s*([\d.]+)/);
        if (!amountMatch) return;
        const amount = Number(amountMatch[1]) || 0;
        if (amount <= 0) return;

        const isAgent =
          (u.agent_name && agentByName.has(u.agent_name.toLowerCase())) ||
          (u.agent_id && agentIds.has(u.agent_id));
        if (!isAgent) return;

        const monthKey = format(new Date(u.created_at), 'MMM');
        monthlyData[monthKey] = (monthlyData[monthKey] || 0) + amount;
      });

      setMonthlyCollections(
        Object.entries(monthlyData).map(([month, amount]) => ({ month, amount }))
      );
    } catch (error) {
      console.error('Error fetching monthly collections:', error);
    } finally {
      setLoadingMonthly(false);
    }
  };

  const fetchTodayCollections = async () => {
    setInsightsLoading(true);
    try {
      const now = new Date();
      const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
      const dayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).toISOString();

      // Fetch agent profiles
      const { data: agentProfiles } = await supabase
        .from('profiles')
        .select('id, username')
        .eq('role', 'agent');

      const agentById: Record<string, string> = {};
      const agentByName: Record<string, string> = {};
      (agentProfiles || []).forEach((a: any) => {
        agentById[a.id] = a.id;
        agentByName[a.username.toLowerCase()] = a.id;
      });

      // Use borrower_updates — guaranteed visibility for supervisors
      const { data } = await supabase
        .from('borrower_updates')
        .select('created_at, content, agent_name, agent_id')
        .ilike('content', 'Payment posted:%')
        .gte('created_at', dayStart)
        .lte('created_at', dayEnd)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(1000);

      const byAgent: Record<string, number> = {};
      (data || []).forEach((u: any) => {
        const amountMatch = u.content?.match(/Payment posted:\s*([\d.]+)/);
        if (!amountMatch) return;
        const amount = Number(amountMatch[1]) || 0;
        if (amount <= 0) return;

        let agentId: string | null = null;
        if (u.agent_name && agentByName[u.agent_name.toLowerCase()]) {
          agentId = agentByName[u.agent_name.toLowerCase()];
        } else if (u.agent_id && agentById[u.agent_id]) {
          agentId = agentById[u.agent_id];
        }

        if (agentId) {
          byAgent[agentId] = (byAgent[agentId] || 0) + amount;
        }
      });
      setTodayByAgent(byAgent);
    } catch (err) {
      console.error('Error fetching today collections:', err);
    } finally {
      setInsightsLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username, role')
        .in('role', ['agent', 'superagent'])
        .order('username');

      if (error) throw error;
      setAgents(data || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const handleAssign = async () => {
    if (!selectedCase || !formData.agent_name) {
      setError('Please select an agent');
      return;
    }

    try {
      // First get the agent's profile ID
      const { data: assignedAgent, error: agentError } = await supabase
        .from('profiles')
        .select('id, username')
        .eq('username', formData.agent_name)
        .single();

      if (agentError || !assignedAgent) throw new Error('Agent not found');

      console.log('Selected agent:', assignedAgent);

      // Update for_collection table with agent_id
      const { data: updateData, error: updateError } = await supabase
        .from('for_collection')
        .update({ 
          agent_id: assignedAgent.id,  // Use agent_id instead of agent_name
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedCase.id)
        .select();

      console.log('Update result:', updateData);

      if (updateError) {
        console.error('Update error:', updateError);
        throw updateError;
      }

      if (!updateData || updateData.length === 0) {
        throw new Error('Failed to update record');
      }

      // Calculate expiry date (30 days from now)
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 30);

      // Create notification for the assigned agent
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert({
          recipient_id: assignedAgent.id,
          message: `You have been assigned to ${selectedCase.borrower?.name} (${selectedCase.status}). Coordinate with Collection Supervisor for more info.`,
          category: 'collection',
          notification_type: 'assignment',
          is_read: false,
          is_active: true,
          reference_id: selectedCase.id,
          expiry_date: expiryDate.toISOString().split('T')[0],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (notificationError) throw notificationError;

      // Update local state
      setUnassignedCases(prev => 
        prev.filter(c => c.id !== selectedCase.id)
      );
      setSelectedCase(null);
      setFormData({ agent_name: '', loandisk_id: '' });
      setError(null);

    } catch (error) {
      console.error('Error assigning case:', error);
      setError('Failed to assign case. Please try again.');
    }
  };

  // Update the handleDismissNotification function to return a Promise
  const handleDismissNotification = async (notificationId: string): Promise<void> => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', notificationId);

      if (error) throw error;
      
      // Update local state to remove the notification
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error dismissing notification:', error);
      throw error;
    }
  };

  // Add a function to handle notification click
  const handleNotificationClick = async (notificationId: string): Promise<void> => {
    const notification = notifications.find(n => n.id === notificationId);
    
    if (!notification || !notification.reference_id) return;
    
    try {
      // Mark as read
      await markAsRead(notificationId);

      // Determine the correct domain - use the current origin
      const targetDomain = window.location.origin;
        
      console.log(`Using target domain: ${targetDomain}`);

      // Handle navigation based on notification type
      if (notification.notification_type === 'itinerary_submitted') {
        // Itinerary submission notification — go to the Approved Itinerary page
        window.location.href = `${targetDomain}/approved-itinerary`;
      }
      else if (notification.notification_type === 'collection_case' && notification.reference_id) {
        // Get the collection ID
        const collectionId = await getCollectionIdFromNotification(notification);
        
        if (collectionId) {
          // Navigate to borrower details
          const url = `${targetDomain}/borrower/${collectionId}`;
          console.log(`Navigating to: ${url}`);
          window.location.href = url;
        } else {
          console.error('Collection ID not found for notification:', notification);
          alert("Unable to locate the collection case information. Please refresh the page and try again.");
        }
      }
      else if (notification.notification_type === 'reminder') {
        // For reminders, get the collection ID
        const collectionId = await getCollectionIdFromNotification(notification);
        
        if (collectionId) {
          // Navigate to the borrower profile
          const url = `${targetDomain}/borrower/${collectionId}`;
          console.log(`Navigating to reminder for collection ID: ${collectionId}`);
          window.location.href = url;
        } else {
          console.error('Could not find collection ID for reminder notification:', notification);
          alert('Unable to navigate to the reminder. Please try accessing it through the borrower profiles page instead.');
        }
      }
      else if (notification.notification_type === 'comment_mention' || 
               notification.notification_type === 'update_mention') {
        // For mentions, we need to get the collection ID
        const collectionId = await getCollectionIdFromNotification(notification);
        if (collectionId) {
          // Navigate to the borrower profile
          const url = `${targetDomain}/borrower/${collectionId}`;
          console.log(`Navigating to: ${url}`);
          window.location.href = url;
        } else {
          console.error('Could not find collection ID for mention notification:', notification);
          alert('Unable to navigate to the referenced content. This could be due to a temporary system issue. Please try again later or contact support if the issue persists.');
        }
      }
    } catch (error) {
      console.error('Error processing notification:', error);
      alert('Failed to process notification. Please try again later.');
    }
  };

  // Add a clearAll function to clear all notifications
  const handleClearAllNotifications = async (): Promise<void> => {
    try {
      // Change from delete to update with deleted_at timestamp
      const { error } = await supabase
        .from('notifications')
        .update({
          is_active: false,
          is_read: true,
          updated_at: new Date().toISOString(),
          deleted_at: new Date().toISOString() // Mark all as permanently deleted
        })
        .eq('recipient_id', user?.id);

      if (error) throw error;
      
      // Update local state
      setNotifications([]);
    } catch (error) {
      console.error('Error clearing all notifications:', error);
      throw error;
    }
  };

  // First, add a function to filter cases by type
  const filterCasesByType = (cases: CollectionCase[], type: 'collection' | 'ci') => {
    return cases.filter(caseItem => 
      type === 'ci' ? caseItem.status === 'For CI' : caseItem.status === 'For Collection'
    );
  };

  // Update the button and status chip styles
  const StatusChip = ({ status }: { status: string }) => (
    <Chip 
      label={status}
      size="small"
      sx={{
        bgcolor: status === 'For Collection' ? 'success.lighter' : 'warning.lighter',
        color: status === 'For Collection' ? 'success.dark' : 'warning.dark',
        fontWeight: 'medium',
        fontSize: isMobile ? '0.65rem' : '0.75rem',
        height: isMobile ? '20px' : '24px',
        '& .MuiChip-label': {
          px: isMobile ? 0.75 : 1.5
        }
      }}
    />
  );

  const AssignButton = ({ onClick }: { onClick: () => void }) => (
    <Button 
      variant="contained"
      size="small"
      onClick={onClick}
      sx={{
        textTransform: 'none',
        boxShadow: 'none',
        borderRadius: 1,
        px: 2,
        py: 0.5,
        fontSize: '0.75rem',
        fontWeight: 'medium',
        bgcolor: 'success.main',
        color: 'white',
        '&:hover': {
          boxShadow: 'none',
          bgcolor: 'success.dark',
        }
      }}
    >
      Assign
    </Button>
  );

  // Fix fetchNotifications function to properly filter dismissed notifications
  const fetchNotifications = async (): Promise<void> => {
    setLoading(true);
    try {
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !currentUser) {
        console.error('Error getting current user:', userError);
        return;
      }
      
      // Fetch the user's notifications with proper filtering - only looking at is_active now
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('recipient_id', currentUser.id)
        .eq('is_active', true)  // Only get active notifications - this is the key filter
        .or('notification_type.eq.collection_case,notification_type.eq.itinerary_submitted,notification_type.eq.comment_mention,notification_type.eq.update_mention,notification_type.eq.reminder')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching notifications:', error);
        return;
      }
      
      // Filter out expired notifications
      if (data) {
        const now = new Date();
        const activeNotifications = data.filter(notification => {
          const expiryDate = new Date(notification.expiry_date);
          return expiryDate > now;
        });
        
        // Update is_active to false for expired notifications
        const expiredNotifications = data.filter(notification => {
          const expiryDate = new Date(notification.expiry_date);
          return expiryDate <= now;
        });
        
        if (expiredNotifications.length > 0) {
          const expiredIds = expiredNotifications.map(n => n.id);
          await supabase
            .from('notifications')
            .update({ 
              is_active: false,
              deleted_at: new Date().toISOString() // Mark as permanently deleted
            })
            .in('id', expiredIds);
        }
        
        setNotifications(activeNotifications);
      } else {
        setNotifications([]);
      }
    } catch (error) {
      console.error('Error in fetchNotifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Add a separate useEffect to fetch notifications when the component mounts
  useEffect(() => {
    console.log('Fetching notifications for collection dashboard');
    fetchNotifications();
  }, []);

  if (!userProfile || userProfile.role !== 'superagent') {
    return <Alert severity="error">Access denied. Superagent access only.</Alert>;
  }

  return (
    <Box sx={{ 
      flexGrow: 1, 
      bgcolor: 'background.default',
      minHeight: '100vh',
      py: isMobile ? 2 : 4
    }}>
      <Container maxWidth="xl" sx={{ px: isMobile ? 1 : 2 }}>
        <Grid container spacing={isMobile ? 1 : 2}>
          {/* Header Section - Update with responsive styling */}
          <Grid item xs={12}>
            <Box sx={{ mb: isMobile ? 0.5 : 2 }}>
              <Typography 
                variant={isMobile ? "h5" : "h3"} 
                sx={{ 
                  fontWeight: 800,
                  mb: isMobile ? 0.5 : 1,
                  background: 'linear-gradient(45deg, #1976d2, #64b5f6)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  fontSize: isMobile ? '1.5rem' : '2.5rem',
                  lineHeight: isMobile ? 1.1 : 1.3,
                }}
              >
                Collection Dashboard
              </Typography>
            </Box>
          </Grid>

          {/* Collection Notifications - Make more compact on mobile */}
          <Grid item xs={12}>
            <NotificationsPanel
              notifications={notifications}
              onDismissNotification={handleDismissNotification}
              onClickNotification={handleNotificationClick}
              onMarkAllAsRead={markAllAsRead}
              onRefresh={fetchNotifications}
              onClearAll={handleClearAllNotifications}
              title="Collection Notifications"
              maxHeight={isMobile ? 300 : 400}
              loading={loading}
              filterCategories={['collection', 'itinerary', 'mention', 'reminder']}
              filterTypes={['assignment', 'collection_case', 'itinerary_submitted', 'comment_mention', 'update_mention', 'reminder']}
            />
          </Grid>

          {/* Stats Sections - Mobile First */}
          <Grid item xs={12}>
          <Grid container spacing={isMobile ? 1 : 2}>

            {/* Total Monthly Collections Card */}
            <Grid item xs={12}>
              <Paper 
                elevation={0}
                sx={{ 
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ 
                  p: isMobile ? 1.5 : 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}10 0%, ${theme.palette.primary.main}05 100%)`,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.75 : 1 }}>
                    <AccountBalance sx={{ 
                      color: theme.palette.primary.main,
                      fontSize: isMobile ? '1.5rem' : '2rem'
                    }} />
                    <Box>
                      {loading ? (
                        <>
                          <Skeleton variant="text" width={isMobile ? 120 : 160} height={isMobile ? 36 : 48} />
                          <Skeleton variant="text" width={100} height={18} />
                        </>
                      ) : (
                        <>
                          <Typography 
                            variant={isMobile ? "h5" : "h4"} 
                            sx={{ 
                              fontWeight: 'bold',
                              fontSize: isMobile ? '1.25rem' : '1.75rem',
                              lineHeight: 1.2,
                            }}
                          >
                            ₱{formatCurrency(totalMonthlyCollection)}
                          </Typography>
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ fontSize: isMobile ? '0.7rem' : '0.8rem' }}
                          >
                            Total Monthly Collections
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        fontSize: isMobile ? '0.7rem' : '0.8rem',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <CalendarMonth 
                        sx={{ 
                          mr: 0.5,
                          fontSize: isMobile ? '0.85rem' : '1rem'
                        }} 
                      />
                      {format(new Date(), 'MMMM yyyy')}
                    </Typography>
                    {teamInsights.teamMonthChangePct !== 0 && (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'flex-end',
                        mt: 0.25,
                        color: teamInsights.teamMonthChangePct >= 0 ? 'success.main' : 'error.main',
                      }}>
                        {teamInsights.teamMonthChangePct >= 0 ? 
                          <ArrowUpward sx={{ fontSize: '0.75rem' }} /> : 
                          <ArrowDownward sx={{ fontSize: '0.75rem' }} />
                        }
                        <Typography variant="caption" sx={{ fontSize: isMobile ? '0.65rem' : '0.7rem' }}>
                          {Math.abs(teamInsights.teamMonthChangePct).toFixed(1)}% vs last month
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Paper>
            </Grid>

            {/* ── Team Momentum Banner ──────────────────────────────────────── */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: isMobile ? 1.25 : 1.75,
                  border: '1px solid',
                  borderColor: teamInsights.teamMonthChangePct >= 0 ? '#4caf5040' : '#ff980040',
                  borderRadius: 2,
                  background: teamInsights.teamMonthChangePct >= 0
                    ? `linear-gradient(135deg, #4caf5012 0%, #4caf5005 100%)`
                    : `linear-gradient(135deg, #ff980012 0%, #ff980005 100%)`,
                }}
              >
                {/* Header row */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <TrendingUp sx={{ fontSize: isMobile ? '1rem' : '1.2rem', color: teamInsights.teamMonthChangePct >= 0 ? '#4caf50' : '#ff9800' }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                      Team Momentum
                    </Typography>
                  </Box>
                  <IconButton size="small" onClick={() => { fetchCollectionSummaries(); fetchTodayCollections(); }} sx={{ p: 0.25 }}>
                    <Refresh sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                  </IconButton>
                </Box>

                {/* Motivational narrative */}
                {loading || insightsLoading ? (
                  <Box sx={{ mb: 1 }}>
                    <Skeleton variant="text" width="90%" height={16} />
                    <Skeleton variant="text" width="75%" height={16} />
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ fontSize: isMobile ? '0.75rem' : '0.8rem', mb: 1, lineHeight: 1.5 }}>
                    {teamInsights.teamMonthChangePct >= 10
                      ? `The team is on fire! ₱${formatCurrency(teamInsights.teamThisMonth)} collected this month — ${teamInsights.teamMonthChangePct.toFixed(1)}% ahead of last month. Keep the momentum!`
                      : teamInsights.teamMonthChangePct >= 0
                      ? `Good progress! ₱${formatCurrency(teamInsights.teamThisMonth)} collected this month, up ${teamInsights.teamMonthChangePct.toFixed(1)}% from last month (₱${formatCurrency(teamInsights.teamLastMonth)}). Push harder!`
                      : teamInsights.teamThisMonth === 0
                      ? `No collections recorded yet this month. Let's get the team moving — every peso counts!`
                      : `The team has collected ₱${formatCurrency(teamInsights.teamThisMonth)} this month. We need ₱${formatCurrency(teamInsights.teamLastMonth - teamInsights.teamThisMonth)} more to match last month (₱${formatCurrency(teamInsights.teamLastMonth)}). Push the team!`
                    }
                  </Typography>
                )}

                {/* Month progress bar vs last month */}
                {teamInsights.teamLastMonth > 0 && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.6rem' : '0.65rem' }}>
                        vs {format(subMonths(new Date(), 1), 'MMMM')} (₱{formatCurrency(teamInsights.teamLastMonth)})
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, fontSize: isMobile ? '0.6rem' : '0.65rem', color: teamInsights.teamMonthChangePct >= 0 ? 'success.main' : 'error.main' }}>
                        {Math.min(Math.round((teamInsights.teamThisMonth / teamInsights.teamLastMonth) * 100), 999)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min((teamInsights.teamThisMonth / teamInsights.teamLastMonth) * 100, 100)}
                      sx={{
                        height: 6, borderRadius: 3,
                        bgcolor: `${teamInsights.teamMonthChangePct >= 0 ? '#4caf50' : '#ff9800'}25`,
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 3,
                          bgcolor: teamInsights.teamMonthChangePct >= 0 ? '#4caf50' : '#ff9800',
                        }
                      }}
                    />
                  </Box>
                )}

                {/* 4-stat row */}
                <Box sx={{ display: 'flex', gap: isMobile ? 0.75 : 1, mt: 1.25, flexWrap: 'wrap' }}>
                  {[
                    { label: 'This Week', value: teamInsights.teamThisWeek, pct: teamInsights.teamWeekChangePct, suffix: 'vs last wk' },
                    { label: 'Last Week', value: teamInsights.teamLastWeek, pct: null, suffix: format(subMonths(new Date(),0), 'wo') + ' week' },
                    { label: 'Active Today', value: Object.keys(todayByAgent).length, pct: null, suffix: `of ${collectorSummaries.length} agents`, isCount: true },
                    { label: 'Today Total', value: Object.values(todayByAgent).reduce((s, v) => s + v, 0), pct: null, suffix: format(new Date(), 'MMM d'), isAmt: true },
                  ].map(stat => (
                    <Box key={stat.label} sx={{ flex: '1 1 0', minWidth: isMobile ? 70 : 90, p: isMobile ? 0.6 : 0.75, bgcolor: 'background.paper', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.55rem' : '0.6rem', display: 'block', mb: 0.2 }}>
                        {stat.label}
                      </Typography>
                      {loading || insightsLoading ? (
                        <Skeleton variant="text" width="80%" height={isMobile ? 18 : 20} />
                      ) : (
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: isMobile ? '0.75rem' : '0.8rem', lineHeight: 1.2 }}>
                        {stat.isCount ? stat.value : `₱${formatCurrency(stat.value as number)}`}
                      </Typography>
                      )}
                      {stat.pct !== null && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.2 }}>
                          {(stat.pct as number) >= 0 ? <ArrowUpward sx={{ fontSize: '0.55rem', color: 'success.main' }} /> : <ArrowDownward sx={{ fontSize: '0.55rem', color: 'error.main' }} />}
                          <Typography variant="caption" sx={{ fontSize: '0.55rem', color: (stat.pct as number) >= 0 ? 'success.main' : 'error.main' }}>
                            {Math.abs(stat.pct as number).toFixed(0)}%
                          </Typography>
                        </Box>
                      )}
                      {stat.pct === null && (
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.55rem' }}>
                          {stat.suffix}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>

                {/* Action chips */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {teamInsights.topPerformer && (
                    <Chip
                      icon={<EmojiEvents sx={{ fontSize: '0.7rem !important' }} />}
                      label={`${teamInsights.topPerformer} leads — ₱${formatCurrency(collectorSummaries[0]?.total_collections || 0)}`}
                      size="small"
                      sx={{ height: 20, fontSize: isMobile ? '0.6rem' : '0.65rem', bgcolor: '#ff980015', color: '#e65100', '& .MuiChip-label': { px: 0.75 } }}
                    />
                  )}
                  {teamInsights.needsAttention.length > 0 && (
                    <Chip
                      label={`Follow up: ${teamInsights.needsAttention.join(', ')}`}
                      size="small"
                      color="warning"
                      sx={{ height: 20, fontSize: isMobile ? '0.6rem' : '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                    />
                  )}
                  <Chip
                    label={`${collectorSummaries.filter(c => c.this_week > 0).length}/${collectorSummaries.length} active this week`}
                    size="small"
                    color="info"
                    sx={{ height: 20, fontSize: isMobile ? '0.6rem' : '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                  />
                </Box>
              </Paper>
            </Grid>

            {/* ── Collectors Leaderboard ───────────────────────────────────── */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}
              >
                {/* Header */}
                <Box sx={{
                  p: isMobile ? 1 : 1.5,
                  borderBottom: '1px solid', borderColor: 'divider',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, transparent 100%)`,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <EmojiEvents sx={{ fontSize: isMobile ? '1.1rem' : '1.3rem', color: '#ff9800' }} />
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, fontSize: isMobile ? '0.875rem' : '1rem', lineHeight: 1.2 }}>
                        Monthly Leaderboard
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.6rem' : '0.65rem' }}>
                        {format(new Date(), 'MMMM yyyy')} · Tap any agent for details
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={`${collectorSummaries.length} agents`}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.6rem', '& .MuiChip-label': { px: 0.75 } }}
                  />
                </Box>

                {/* Podium — loading skeleton */}
                {loading && (
                  <Box sx={{ px: isMobile ? 1 : 2, pt: isMobile ? 1 : 1.5, pb: 0.5, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: isMobile ? 0.75 : 1.5 }}>
                    {[28, 38, 28].map((widthPct, i) => (
                      <Box key={i} sx={{ flex: `0 0 ${widthPct}%`, textAlign: 'center' }}>
                        <Skeleton variant="circular" width={isMobile ? 40 : 52} height={isMobile ? 40 : 52} sx={{ mx: 'auto', mb: 0.5 }} />
                        <Skeleton variant="text" width="70%" sx={{ mx: 'auto' }} />
                        <Skeleton variant="rounded" width="100%" height={i === 1 ? (isMobile ? 64 : 80) : (isMobile ? 44 : 56)} />
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Podium — top 3 */}
                {!loading && collectorSummaries.length >= 3 && (
                  <Box sx={{
                    px: isMobile ? 1 : 2,
                    pt: isMobile ? 1 : 1.5,
                    pb: 0.5,
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    gap: isMobile ? 0.75 : 1.5,
                  }}>
                    {/* Silver — #2 */}
                    {[1, 0, 2].map((podiumIdx) => {
                      const s = collectorSummaries[podiumIdx];
                      const podiumRank = podiumIdx === 0 ? 1 : podiumIdx === 1 ? 2 : 3;
                      const heights = { 1: isMobile ? 64 : 80, 2: isMobile ? 48 : 60, 3: isMobile ? 40 : 52 };
                      const colors = { 1: '#FFD700', 2: '#C0C0C0', 3: '#CD7F32' };
                      const bgColors = { 1: '#FFF8E1', 2: '#F5F5F5', 3: '#FFF3E0' };
                      const todayAmt = todayByAgent[s.collector_id] || 0;
                      return (
                        <Box
                          key={s.collector_id}
                          onClick={() => setExpandedAgent(expandedAgent === s.collector_id ? null : s.collector_id)}
                          sx={{
                            flex: podiumIdx === 0 ? '0 0 38%' : '0 0 28%',
                            textAlign: 'center',
                            cursor: 'pointer',
                          }}
                        >
                          {/* Medal badge */}
                          <Box sx={{ position: 'relative', display: 'inline-block', mb: 0.5 }}>
                            <Avatar
                              src={s.avatar_url || undefined}
                              sx={{
                                width: podiumIdx === 0 ? (isMobile ? 44 : 52) : (isMobile ? 36 : 44),
                                height: podiumIdx === 0 ? (isMobile ? 44 : 52) : (isMobile ? 36 : 44),
                                bgcolor: colors[podiumRank as keyof typeof colors],
                                color: '#333',
                                fontWeight: 800,
                                fontSize: podiumIdx === 0 ? (isMobile ? '1rem' : '1.2rem') : (isMobile ? '0.8rem' : '1rem'),
                                border: `3px solid ${colors[podiumRank as keyof typeof colors]}`,
                                boxShadow: podiumIdx === 0 ? `0 0 0 3px ${colors[1]}40` : 'none',
                                mx: 'auto',
                              }}
                            >
                              {!s.avatar_url && s.collector_name.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box sx={{
                              position: 'absolute', bottom: -4, right: -4,
                              width: isMobile ? 16 : 18, height: isMobile ? 16 : 18,
                              borderRadius: '50%',
                              bgcolor: colors[podiumRank as keyof typeof colors],
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              border: '1px solid white',
                            }}>
                              <Typography sx={{ fontWeight: 900, fontSize: isMobile ? '0.5rem' : '0.55rem', color: '#333' }}>
                                {podiumRank}
                              </Typography>
                            </Box>
                          </Box>
                          {/* Name */}
                          <Typography sx={{
                            fontWeight: podiumIdx === 0 ? 800 : 600,
                            fontSize: isMobile ? '0.65rem' : '0.72rem',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            maxWidth: '100%',
                          }}>
                            {s.collector_name}
                          </Typography>
                          {/* Podium base */}
                          <Box sx={{
                            height: heights[podiumRank as keyof typeof heights],
                            bgcolor: bgColors[podiumRank as keyof typeof bgColors],
                            border: `2px solid ${colors[podiumRank as keyof typeof colors]}`,
                            borderRadius: '4px 4px 0 0',
                            mt: 0.5,
                            display: 'flex', flexDirection: 'column',
                            alignItems: 'center', justifyContent: 'center',
                            px: 0.5,
                          }}>
                            <Typography sx={{ fontWeight: 800, fontSize: isMobile ? '0.72rem' : '0.8rem', color: '#333', lineHeight: 1.2 }}>
                              ₱{formatCurrency(s.total_collections)}
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: isMobile ? '0.5rem' : '0.55rem', color: 'text.secondary', lineHeight: 1.2 }}>
                              this month
                            </Typography>
                            {todayAmt > 0 && (
                              <Chip
                                label={`+₱${formatCurrency(todayAmt)} today`}
                                size="small"
                                sx={{ mt: 0.3, height: 14, fontSize: '0.48rem', bgcolor: '#4caf5020', color: '#2e7d32', '& .MuiChip-label': { px: 0.5 } }}
                              />
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* Full ranked list */}
                <Box sx={{ display: 'flex', flexDirection: 'column', mt: collectorSummaries.length >= 3 ? 0 : 0 }}>
                  {collectorSummaries.map((summary, index) => {
                    const topAmount = collectorSummaries[0]?.total_collections || 1;
                    const progressPct = (summary.total_collections / topAmount) * 100;
                    const gapToNext = index > 0 ? (collectorSummaries[index - 1].total_collections - summary.total_collections) : 0;
                    const todayAmt = todayByAgent[summary.collector_id] || 0;
                    const medalColors = ['#FFD700', '#C0C0C0', '#CD7F32'];

                    return (
                      <Box key={summary.collector_id}>
                        {/* Agent Row */}
                        <Box
                          onClick={() => setExpandedAgent(expandedAgent === summary.collector_id ? null : summary.collector_id)}
                          sx={{
                            display: 'flex', alignItems: 'center',
                            py: isMobile ? 0.75 : 1,
                            px: isMobile ? 1 : 1.5,
                            cursor: 'pointer',
                            transition: 'background 0.15s',
                            '&:hover': { bgcolor: 'action.hover' },
                            '&:active': { bgcolor: 'action.selected' },
                            ...(index !== collectorSummaries.length - 1 && expandedAgent !== summary.collector_id && {
                              borderBottom: '1px solid', borderColor: 'divider'
                            }),
                          }}
                        >
                          {/* Rank number + icon */}
                          <Box sx={{ width: isMobile ? 28 : 36, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.25 }}>
                            <Typography variant="caption" sx={{
                              fontWeight: 800, fontSize: isMobile ? '0.7rem' : '0.75rem',
                              color: index < 3 ? medalColors[index] : 'text.disabled',
                              minWidth: 14,
                            }}>
                              {index + 1}
                            </Typography>
                            {index === 0 && <EmojiEvents sx={{ fontSize: isMobile ? '0.8rem' : '0.9rem', color: '#FFD700' }} />}
                            {index === 1 && <EmojiEvents sx={{ fontSize: isMobile ? '0.8rem' : '0.9rem', color: '#C0C0C0' }} />}
                            {index === 2 && <EmojiEvents sx={{ fontSize: isMobile ? '0.8rem' : '0.9rem', color: '#CD7F32' }} />}
                            {index >= 3 && <Person sx={{ fontSize: isMobile ? '0.75rem' : '0.85rem', color: 'text.disabled' }} />}
                          </Box>

                          {/* Name + progress bar + today badge */}
                          <Box sx={{ flex: 1, minWidth: 0, mx: isMobile ? 0.75 : 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                              <Typography variant="body2" sx={{
                                fontWeight: index < 3 ? 700 : 500,
                                fontSize: isMobile ? '0.78rem' : '0.83rem',
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                flex: 1,
                              }}>
                                {summary.collector_name}
                              </Typography>
                              {todayAmt > 0 && (
                                <Chip
                                  label={`+₱${formatCurrency(todayAmt)}`}
                                  size="small"
                                  sx={{ height: 16, fontSize: '0.55rem', bgcolor: '#4caf5018', color: '#2e7d32', flexShrink: 0, '& .MuiChip-label': { px: 0.6 } }}
                                />
                              )}
                            </Box>
                            {/* Progress bar vs #1 */}
                            <LinearProgress
                              variant="determinate"
                              value={progressPct}
                              sx={{
                                height: 4, borderRadius: 2,
                                bgcolor: `${index < 3 ? medalColors[index] : theme.palette.primary.main}20`,
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 2,
                                  bgcolor: index < 3 ? medalColors[index] : theme.palette.primary.main,
                                }
                              }}
                            />
                            {/* Week trend + gap to next */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.15 }}>
                                {summary.week_change_pct >= 0
                                  ? <ArrowUpward sx={{ fontSize: '0.5rem', color: 'success.main' }} />
                                  : <ArrowDownward sx={{ fontSize: '0.5rem', color: 'error.main' }} />}
                                <Typography variant="caption" sx={{ fontSize: isMobile ? '0.52rem' : '0.57rem', color: summary.week_change_pct >= 0 ? 'success.main' : 'error.main' }}>
                                  {summary.week_change_pct >= 0 ? '+' : ''}{summary.week_change_pct.toFixed(0)}% wk
                                </Typography>
                              </Box>
                              {gapToNext > 0 && (
                                <Typography variant="caption" sx={{ fontSize: isMobile ? '0.52rem' : '0.57rem', color: 'text.disabled' }}>
                                  · ₱{formatCurrency(gapToNext)} to #{index}
                                </Typography>
                              )}
                            </Box>
                          </Box>

                          {/* Monthly total */}
                          <Typography variant="body2" sx={{
                            fontWeight: 700,
                            color: summary.total_collections > 0 ? 'success.main' : 'text.disabled',
                            fontSize: isMobile ? '0.78rem' : '0.83rem',
                            textAlign: 'right', flexShrink: 0,
                          }}>
                            ₱{formatCurrency(summary.total_collections)}
                          </Typography>
                        </Box>

                        {/* Expanded Details */}
                        {expandedAgent === summary.collector_id && (
                          <Box sx={{
                            px: isMobile ? 1 : 1.5, py: isMobile ? 0.75 : 1,
                            bgcolor: 'background.default',
                            borderBottom: '1px solid', borderColor: 'divider',
                          }}>
                            <Grid container spacing={isMobile ? 0.5 : 1}>
                              {[
                                { label: 'This Week', value: summary.this_week, pct: summary.week_change_pct, pctLabel: 'vs last week' },
                                { label: 'Last Week', value: summary.last_week, pct: null, pctLabel: '' },
                                { label: 'This Month', value: summary.this_month, pct: summary.month_change_pct, pctLabel: 'vs last month' },
                                { label: 'Last Month', value: summary.last_month, pct: null, pctLabel: '' },
                              ].map(stat => (
                                <Grid item xs={6} key={stat.label}>
                                  <Box sx={{ p: 0.75, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', display: 'block' }}>{stat.label}</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: isMobile ? '0.78rem' : '0.83rem' }}>
                                      ₱{formatCurrency(stat.value)}
                                    </Typography>
                                    {stat.pct !== null && (
                                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.2 }}>
                                        {(stat.pct as number) >= 0 ? <ArrowUpward sx={{ fontSize: '0.5rem', color: 'success.main' }} /> : <ArrowDownward sx={{ fontSize: '0.5rem', color: 'error.main' }} />}
                                        <Typography variant="caption" sx={{ fontSize: '0.55rem', color: (stat.pct as number) >= 0 ? 'success.main' : 'error.main' }}>
                                          {Math.abs(stat.pct as number).toFixed(1)}% {stat.pctLabel}
                                        </Typography>
                                      </Box>
                                    )}
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>

                            {/* Contextual motivational message */}
                            <Box sx={{
                              mt: 0.75, p: 0.75,
                              borderRadius: 1,
                              bgcolor: summary.week_change_pct >= 10 ? '#4caf5012'
                                : summary.week_change_pct >= 0 ? '#2196f312'
                                : summary.this_week === 0 ? '#f4433612'
                                : '#ff980012',
                              borderLeft: `3px solid ${summary.week_change_pct >= 10 ? '#4caf50' : summary.week_change_pct >= 0 ? '#2196f3' : summary.this_week === 0 ? '#f44336' : '#ff9800'}`,
                            }}>
                              <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1.5 }}>
                                {summary.week_change_pct >= 20
                                  ? `🔥 ${summary.collector_name} is on fire — up ${summary.week_change_pct.toFixed(0)}% this week! Rank #${collectorSummaries.indexOf(summary) + 1} of ${collectorSummaries.length}. Celebrate this!`
                                  : summary.week_change_pct >= 0 && summary.this_week > 0
                                  ? `👍 ${summary.collector_name} is trending up. Ranked #${collectorSummaries.indexOf(summary) + 1}. ${gapToNext > 0 ? `Only ₱${formatCurrency(gapToNext)} away from rank #${collectorSummaries.indexOf(summary)}.` : 'Leading the board!'}`
                                  : summary.this_week === 0 && summary.last_week === 0
                                  ? `⚠️ ${summary.collector_name} has no recent collections. Reach out and check in — reassign cases if needed.`
                                  : summary.this_week === 0 && summary.last_week > 0
                                  ? `🚨 ${summary.collector_name} had ₱${formatCurrency(summary.last_week)} last week but nothing this week. Immediate follow-up required.`
                                  : `📉 ${summary.collector_name} is down ${Math.abs(summary.week_change_pct).toFixed(0)}% this week vs last. Consider route review or motivation boost.`
                                }
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Paper>
            </Grid>

            {/* ── Tips & Motivation ────────────────────────────────────────── */}
            <Grid item xs={12}>
              {/* Use Box flex to avoid Grid spacing negative-margin overlap on mobile */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 1 : 1.5 }}>
                {/* Supervisor Coaching Tip */}
                <Paper
                  elevation={0}
                  sx={{
                    flex: { xs: '0 0 100%', sm: '1 1 calc(50% - 6px)' },
                    minWidth: 0,
                    p: isMobile ? 1.25 : 1.5,
                    border: '1px solid',
                    borderColor: '#2196f340',
                    borderRadius: 2,
                    background: `linear-gradient(135deg, #2196f310 0%, #2196f305 100%)`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: isMobile ? 0.75 : 1 }}>
                    <Box sx={{ p: isMobile ? 0.6 : 0.75, borderRadius: 1.5, bgcolor: '#2196f325', flexShrink: 0 }}>
                      <Groups sx={{ color: '#1565c0', fontSize: isMobile ? '1rem' : '1.2rem' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.4 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: isMobile ? '0.75rem' : '0.8rem', color: '#0d47a1' }}>
                          Supervisor Coaching Tip
                        </Typography>
                        <IconButton size="small" onClick={() => setCurrentTip(getContextualTip())} sx={{ p: 0.2 }}>
                          <Refresh sx={{ fontSize: '0.85rem', color: '#1565c0' }} />
                        </IconButton>
                      </Box>
                      <Typography variant="body2" sx={{ fontSize: isMobile ? '0.72rem' : '0.78rem', lineHeight: 1.55, color: 'text.primary' }}>
                        {supervisorTip.text}
                      </Typography>
                      <Chip
                        label={supervisorTip.category}
                        size="small"
                        sx={{ mt: 0.5, height: 16, fontSize: '0.52rem', bgcolor: '#1976d220', color: '#0d47a1', textTransform: 'capitalize', '& .MuiChip-label': { px: 0.75 } }}
                      />
                    </Box>
                  </Box>
                </Paper>

                {/* Collector Motivation Tip (to share with the team) */}
                <Paper
                  elevation={0}
                  sx={{
                    flex: { xs: '0 0 100%', sm: '1 1 calc(50% - 6px)' },
                    minWidth: 0,
                    p: isMobile ? 1.25 : 1.5,
                    border: '1px solid',
                    borderColor: '#ff980040',
                    borderRadius: 2,
                    background: `linear-gradient(135deg, #ff980010 0%, #ff980005 100%)`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: isMobile ? 0.75 : 1 }}>
                    <Box sx={{ p: isMobile ? 0.6 : 0.75, borderRadius: 1.5, bgcolor: '#ff980025', flexShrink: 0 }}>
                      <Lightbulb sx={{ color: '#e65100', fontSize: isMobile ? '1rem' : '1.2rem' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.4 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: isMobile ? '0.75rem' : '0.8rem', color: '#e65100' }}>
                          Share with Your Team
                        </Typography>
                        <IconButton size="small" onClick={() => setCurrentTip(getContextualTip())} sx={{ p: 0.2 }}>
                          <Refresh sx={{ fontSize: '0.85rem', color: '#ff9800' }} />
                        </IconButton>
                      </Box>
                      <Typography variant="body2" sx={{ fontSize: isMobile ? '0.72rem' : '0.78rem', lineHeight: 1.55, color: 'text.primary' }}>
                        {currentTip.text}
                      </Typography>
                      <Chip
                        label={currentTip.category}
                        size="small"
                        sx={{ mt: 0.5, height: 16, fontSize: '0.52rem', bgcolor: '#ff980020', color: '#e65100', textTransform: 'capitalize', '& .MuiChip-label': { px: 0.75 } }}
                      />
                    </Box>
                  </Box>
                </Paper>
              </Box>
            </Grid>

            {/* Monthly Collections Chart - HIDE ON MOBILE */}
            {!isMobile && (
              <Grid item xs={12} md={6}>
                <Paper 
                  elevation={0}
                  sx={{ 
                    p: 1.5,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 3 }}>
                    Monthly Collections Trend
                  </Typography>
                  <Box sx={{ height: 300 }}>
                    <Bar
                      data={{
                        labels: monthlyCollections.map(item => item.month),
                        datasets: [
                          {
                            label: 'Total Collections',
                            data: monthlyCollections.map(item => item.amount),
                            backgroundColor: `${theme.palette.primary.main}80`,
                            borderColor: theme.palette.primary.main,
                            borderWidth: 1,
                            borderRadius: 6,
                            maxBarThickness: 40,
                          }
                        ]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: false,
                          },
                          title: {
                            display: false,
                          },
                          tooltip: {
                            backgroundColor: theme.palette.background.paper,
                            titleColor: theme.palette.text.primary,
                            bodyColor: theme.palette.text.secondary,
                            borderColor: theme.palette.divider,
                            borderWidth: 1,
                            padding: 12,
                            cornerRadius: 8,
                            callbacks: {
                              label: (context: any) => `₱${formatCurrency(context.raw as number)}`
                            }
                          }
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            grid: {
                              color: theme.palette.divider,
                            },
                            border: {
                              display: false
                            },
                            ticks: {
                              callback: (value: any) => `₱${formatCurrency(value as number)}`,
                              font: {
                                size: 11
                              }
                            }
                          },
                          x: {
                            grid: {
                              display: false
                            },
                            border: {
                              display: false
                            },
                            ticks: {
                              font: {
                                size: 11
                              }
                            }
                          }
                        }
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>
          </Grid>

          {/* Unassigned Cases Section */}
          <Grid item xs={12}>
            <Paper 
              elevation={0}
              sx={{ 
                p: isMobile ? 1 : 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2
              }}
            >
              {/* For Collection Cases heading with reduced spacing */}
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 'bold', 
                  mb: isMobile ? 1 : 3,
                  fontSize: isMobile ? '1rem' : 'inherit'
                }}
              >
                For Collection Cases
              </Typography>
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : filterCasesByType(unassignedCases, 'collection').length === 0 ? (
                <Alert 
                  severity="info"
                  sx={{
                    borderRadius: 2
                  }}
                >
                  No unassigned collection cases
                </Alert>
              ) : (
                <TableContainer sx={{ overflow: 'auto' }}>
                  <Table size={isMobile ? "small" : "medium"} sx={{ tableLayout: isMobile ? 'fixed' : 'auto' }}>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell sx={{ width: isMobile ? '40%' : 'auto' }}>
                          Borrower Name
                        </StyledTableCell>
                        {!isMobile && (
                          <>
                            <StyledTableCell>Mobile No.</StyledTableCell>
                            <StyledTableCell>Email</StyledTableCell>
                            <StyledTableCell>Address</StyledTableCell>
                            <StyledTableCell>Agent</StyledTableCell>
                            <StyledTableCell>Loandisk ID</StyledTableCell>
                          </>
                        )}
                        <StyledTableCell sx={{ width: isMobile ? '30%' : 'auto' }}>
                          Status
                        </StyledTableCell>
                        <StyledTableCell align="center" sx={{ width: isMobile ? '30%' : 'auto' }}>
                          Actions
                        </StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filterCasesByType(unassignedCases, 'collection').map((caseItem) => (
                        <TableRow 
                          key={caseItem.id}
                          sx={{
                            '&:hover': {
                              bgcolor: 'action.hover'
                            }
                          }}
                        >
                          <TableCell 
                            sx={isMobile ? { 
                              py: 0.75, 
                              px: 0.5,
                              maxWidth: 0, // Force truncation
                              width: '40%'
                            } : {}}
                          >
                            <Typography sx={{ 
                              fontWeight: 'bold',
                              fontSize: isMobile ? '0.75rem' : 'inherit',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '100%' // Ensure text doesn't overflow cell
                            }}>
                              {caseItem.borrower?.name || 'N/A'}
                            </Typography>
                          </TableCell>
                          {!isMobile && (
                            <>
                              <TableCell>{caseItem.borrower?.mobile_no || 'N/A'}</TableCell>
                              <TableCell>{caseItem.borrower?.email || 'N/A'}</TableCell>
                              <TableCell>{caseItem.borrower?.address || 'N/A'}</TableCell>
                              <TableCell>{caseItem.agent_id ? 'Assigned' : 'Unassigned'}</TableCell>
                              <TableCell>{caseItem.loandisk_id || 'N/A'}</TableCell>
                            </>
                          )}
                          <TableCell sx={isMobile ? { 
                            py: 0.75, 
                            px: 0.5,
                            width: '30%'
                          } : {}}>
                            <StatusChip status={caseItem.status} />
                          </TableCell>
                          <TableCell 
                            align="center" 
                            sx={isMobile ? { 
                              py: 0.75, 
                              px: 0.25,
                              width: '30%'
                            } : {}}
                          >
                            <Button 
                              variant="contained"
                              size="small"
                              onClick={() => setSelectedCase(caseItem)}
                              sx={{
                                textTransform: 'none',
                                boxShadow: 'none',
                                borderRadius: 1,
                                px: isMobile ? 1 : 2,
                                py: isMobile ? 0.25 : 0.5,
                                fontSize: isMobile ? '0.7rem' : '0.75rem',
                                fontWeight: 'medium',
                                bgcolor: 'success.main',
                                color: 'white',
                                minWidth: isMobile ? '60px' : 'auto',
                                '&:hover': {
                                  boxShadow: 'none',
                                  bgcolor: 'success.dark',
                                }
                              }}
                            >
                              Assign
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* For CI Cases - Also simplified for mobile */}
              <Typography variant="h6" sx={{ fontWeight: 'bold', mb: isMobile ? 1.5 : 3, mt: 4 }}>
                For CI Cases
              </Typography>
              
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : filterCasesByType(unassignedCases, 'ci').length === 0 ? (
                <Alert 
                  severity="info"
                  sx={{
                    borderRadius: 2
                  }}
                >
                  No unassigned CI cases
                </Alert>
              ) : (
                <TableContainer sx={{ overflow: 'auto' }}>
                  <Table size={isMobile ? "small" : "medium"}>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>Borrower Name</StyledTableCell>
                        {!isMobile && (
                          <>
                            <StyledTableCell>Mobile No.</StyledTableCell>
                            <StyledTableCell>Email</StyledTableCell>
                            <StyledTableCell>Address</StyledTableCell>
                            <StyledTableCell>Agent Name</StyledTableCell>
                            <StyledTableCell>Loandisk ID</StyledTableCell>
                          </>
                        )}
                        <StyledTableCell>Status</StyledTableCell>
                        <StyledTableCell align="center">Actions</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filterCasesByType(unassignedCases, 'ci').map((caseItem) => (
                        <TableRow 
                          key={caseItem.id}
                          sx={{
                            '&:hover': {
                              bgcolor: 'action.hover'
                            }
                          }}
                        >
                          <TableCell sx={isMobile ? { py: 1, px: 0.5 } : {}}>
                            <Typography sx={{ 
                              fontWeight: 'bold',
                              fontSize: isMobile ? '0.8rem' : 'inherit',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              maxWidth: isMobile ? '120px' : 'none',
                              whiteSpace: 'nowrap'
                            }}>
                              {caseItem.borrower?.name || 'N/A'}
                            </Typography>
                          </TableCell>
                          {!isMobile && (
                            <>
                              <TableCell>{caseItem.borrower?.mobile_no || 'N/A'}</TableCell>
                              <TableCell>{caseItem.borrower?.email || 'N/A'}</TableCell>
                              <TableCell>{caseItem.borrower?.address || 'N/A'}</TableCell>
                              <TableCell>{caseItem.agent_id || 'Unassigned'}</TableCell>
                              <TableCell>{caseItem.loandisk_id || 'N/A'}</TableCell>
                            </>
                          )}
                          <TableCell sx={isMobile ? { py: 1, px: 0.5 } : {}}>
                            <StatusChip status={caseItem.status} />
                          </TableCell>
                          <TableCell sx={isMobile ? { py: 1, px: 0.5 } : {}}>
                            <AssignButton onClick={() => setSelectedCase(caseItem)} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Assignment Dialog - Make more compact on mobile */}
      <Dialog 
        open={!!selectedCase} 
        onClose={() => setSelectedCase(null)}
        aria-labelledby="assign-dialog-title"
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxWidth: isMobile ? '90vw' : 500,
            m: isMobile ? 1 : 2
          }
        }}
      >
        <DialogTitle id="assign-dialog-title" sx={{ pb: 1, fontWeight: 'bold' }}>
          Assign Collection Case
        </DialogTitle>
        
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                mb: 3,
                color: 'text.secondary',
                fontWeight: 'medium'
              }}
            >
              Borrower: {selectedCase?.borrower?.name || 'N/A'}
            </Typography>
            
            <TextField
              select
              fullWidth
              label="Assign to Agent"
              value={formData.agent_name}
              onChange={(e) => {
                console.log('Selected agent name:', e.target.value); // Debug log
                setFormData(prev => ({ 
                  ...prev, 
                  agent_name: e.target.value 
                }));
              }}
              error={!!error}
              helperText={error}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      '& .MuiMenuItem-root': {
                        py: 1
                      }
                    }
                  }
                }
              }}
            >
              {agents.map((agent) => (
                <MenuItem 
                  key={agent.id} 
                  value={agent.username}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Person sx={{ 
                    fontSize: '1.2rem',
                    color: 'primary.main'
                  }} />
                  <Box>
                    <Typography variant="body2">
                      {agent.username}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ textTransform: 'capitalize' }}
                    >
                      {agent.role}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => {
              setSelectedCase(null);
              setFormData({ agent_name: '', loandisk_id: '' });
              setError(null);
            }}
            sx={{ 
              color: 'text.secondary',
              '&:hover': {
                bgcolor: 'grey.100'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleAssign} 
            variant="contained"
            disabled={!formData.agent_name}
            sx={{
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none'
              }
            }}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CollectionDashboard;
